import React, { useState, useEffect } from 'react';
import { Bot } from '../types/api';
import { botAPI } from '../services/api';
import BotFormModal, { BotFormData, botToFormData, formDataToPayload } from './BotFormModal';

interface EditBotModalProps {
  bot: Bot;
  onClose: () => void;
  onBotUpdated: (bot: Bot) => void;
}

const EditBotModal: React.FC<EditBotModalProps> = ({ bot, onClose, onBotUpdated }) => {
  const [formData, setFormData] = useState<BotFormData>(botToFormData(bot));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize form with bot data when bot prop changes
  useEffect(() => {
    if (bot) {
      setFormData(botToFormData(bot));
    }
  }, [bot]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate mandatory fields
    if (formData.testnet) {
      if (!formData.binance_testnet_api_key || !formData.binance_testnet_secret_key) {
        setError('Binance Testnet API Key and Secret are required');
        setLoading(false);
        return;
      }
    } else {
      if (!formData.binance_api_key || !formData.binance_secret_key) {
        setError('Binance API Key and Secret are required');
        setLoading(false);
        return;
      }
    }

    if (!formData.gemini_api_key) {
      setError('Gemini API Key is required');
      setLoading(false);
      return;
    }

    if (formData.news_fetcher && !formData.cryptopanic_api_key) {
      setError('CryptoPanic API Key is required when News Fetching is enabled');
      setLoading(false);
      return;
    }

    if (formData.telegram_notifications && (!formData.telegram_bot_token || !formData.telegram_chat_id)) {
      setError('Telegram Bot Token and Chat ID are required when Telegram Notifications are enabled');
      setLoading(false);
      return;
    }

    try {
      const payload = formDataToPayload(formData);
      const updatedBot = await botAPI.updateBot(bot.id, payload);
      onBotUpdated(updatedBot);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update bot');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BotFormModal
      title="Edit Trading Bot"
      submitText="Update Bot"
      loadingText="Updating..."
      formData={formData}
      setFormData={setFormData}
      onSubmit={handleSubmit}
      onClose={onClose}
      loading={loading}
      error={error}
      isEdit={true}
    />
  );
};

export default EditBotModal;